import { useEffect, useState } from "react";
import { Button, Form, Header, Icon, Label, Loader, Message, Modal, Segment } from "semantic-ui-react";
import { billApi, chargeItemApi } from "../../../api/api";
import { mapBillPrint, mapChargeItem } from "../../../api/mappers";
import { printHtml } from "../../../utils/printHtml";
import { formatRupees } from "../../../utils/money";
import BillRoomCharges from "./BillRoomCharges";
import BillRetention from "./BillRetention";
import BillMealsGrid from "./BillMealsGrid";
import BillExtras from "./BillExtras";
import BillTotals from "./BillTotals";
import BillPayments from "./BillPayments";
import { billHtml } from "./billPrint";
import { BillRevisions, MarkPaidButton, PaidLabel } from "./BillStatusExtras";

/* Booking statuses (as the booking mapper names them) that can carry a bill. */
export const BILLABLE_BOOKING_STATUSES = new Set(["Approved", "Partially Confirmed", "Checked In", "Checked Out"]);

export const STATUS_LABEL = { DRAFT: "Draft", FINALIZED: "Final", CANCELLED: "Cancelled" };
const STATUS_COLOR = { DRAFT: "yellow", FINALIZED: "green", CANCELLED: "grey" };

/* What still stands between a draft and "Finalize", in words — mirrors the
   server's refusals so the button explains itself instead of failing. */
export function finalizeBlockers(bill) {
  const out = [];
  if (bill.isStale) out.push("The booking changed since this bill was worked out — refresh it.");
  if (bill.lines.some((l) => l.kind === "ROOM" && l.detail.estimated)) {
    out.push("A room has not checked out yet.");
  }
  const undecided = bill.earlyCheckouts.filter((e) => !e.decided).length;
  if (undecided) out.push(`Answer the retention question for ${undecided} room(s).`);
  return out;
}

/* One booking's bill as a page: a sticky bar with the actions, the charges
   on the left, and totals + payments kept in view on the right — so money
   taken shows up the moment it is recorded. Works before any bill exists
   too: advances can be taken, and "Make bill" starts one. */
export default function BillView({ booking, billState, can, canMakeBill, onBack }) {
  const { bill, noBill, loading, busy, error } = billState;
  const [items, setItems] = useState([]);
  const [reasonFor, setReasonFor] = useState(null); // "regenerate" | "cancel"
  const [confirmFinal, setConfirmFinal] = useState(false);
  const [printError, setPrintError] = useState("");
  const editable = bill?.status === "DRAFT" && can("bill.edit");
  const ghId = bill?.guestHouseId;

  useEffect(() => {
    let live = true;
    if (ghId) {
      chargeItemApi.list(ghId)
        .then((data) => live && setItems(data.map(mapChargeItem)))
        .catch(() => live && setItems([]));
    }
    return () => { live = false; };
  }, [ghId]);

  const print = async () => {
    setPrintError("");
    try {
      const data = mapBillPrint(await billApi.print(booking.id));
      printHtml(billHtml(data), `Bill ${data.bill.billNumber}`);
    } catch (e) {
      setPrintError(e.message || "Could not prepare the bill for printing.");
    }
  };

  const blockers = bill ? finalizeBlockers(bill) : [];
  const payments = (
    <BillPayments
      payments={billState.payments} summary={billState.paymentSummary}
      hasBill={Boolean(bill)} billFinal={bill?.status === "FINALIZED"}
      canRecord={can("payment.record")} busy={busy}
      onRecord={billState.payment.record} onVoid={billState.payment.void}
    />
  );

  return (
    <div className="bhu-bill-page">
      <style>{`
        .bhu-bill-page .bhu-bill-bar { position: sticky; top: 0; z-index: 5; background: #fff;
          display: flex; align-items: center; gap: 10px; flex-wrap: wrap; padding: 10px 14px;
          border-bottom: 1px solid rgba(34,36,38,.15); margin-bottom: 12px; }
        .bhu-bill-page .bhu-bill-body { display: grid; grid-template-columns: minmax(0, 1fr) 420px; gap: 14px; align-items: start; }
        .bhu-bill-page .bhu-bill-side { position: sticky; top: 70px; display: flex; flex-direction: column; gap: 12px; }
        .bhu-bill-page .ui.segment { margin: 0 0 12px; }
        @media (max-width: 1100px) {
          .bhu-bill-page .bhu-bill-body { grid-template-columns: 1fr; }
          .bhu-bill-page .bhu-bill-side { position: static; }
        }
      `}</style>

      <div className="bhu-bill-bar">
        <Button icon circular basic type="button" onClick={onBack} title="Back to the booking" aria-label="Back to the booking">
          <Icon name="arrow left" />
        </Button>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span style={{ fontSize: "0.75rem", opacity: 0.6 }}>{booking.guestName} · {booking.bookingCode}</span>
          <strong style={{ fontSize: "1.15rem" }}>{bill ? `Bill ${bill.billNumber}` : "Bill"}</strong>
        </div>
        {bill && <Label color={STATUS_COLOR[bill.status]}>{STATUS_LABEL[bill.status]}</Label>}
        {bill?.isOfficial && <Label basic color="blue">Official booking — no GST</Label>}
        <PaidLabel bill={bill} summary={billState.paymentSummary} />
        <div style={{ marginLeft: "auto", display: "flex", gap: 8, flexWrap: "wrap" }}>
          {bill && <Button onClick={print}><Icon name="print" />Print</Button>}
          {bill?.status === "DRAFT" && can("bill.finalize") && (
            <>
              <Button basic negative disabled={busy} onClick={() => setReasonFor("cancel")}>Cancel bill</Button>
              <Button positive disabled={busy || blockers.length > 0} onClick={() => setConfirmFinal(true)}
                title={blockers.join(" ")}>
                <Icon name="lock" />Finalize bill
              </Button>
            </>
          )}
          {bill?.status === "FINALIZED" && can("payment.record") && (
            <MarkPaidButton summary={billState.paymentSummary} busy={busy} onRecord={billState.payment.record} />
          )}
          {bill?.status === "FINALIZED" && can("bill.finalize") && (
            <Button basic disabled={busy} onClick={() => setReasonFor("regenerate")}>
              <Icon name="redo" />Correct (new revision)
            </Button>
          )}
        </div>
      </div>

      {error && <Message negative onDismiss={billState.clearError} content={error} />}
      {printError && <Message negative onDismiss={() => setPrintError("")} content={printError} />}
      {bill?.status === "DRAFT" && blockers.length > 0 && can("bill.finalize") && (
        <Message warning size="small" icon="info circle" header="Before this bill can be finalized"
          list={blockers} />
      )}

      {loading && !bill && !noBill && <Loader active inline="centered" content="Loading the bill…" />}

      {noBill && (
        <div className="bhu-bill-body">
          <Segment>
            <Header as="h4">No bill yet</Header>
            <p>Advances can be taken now; the bill is made when the stay is ready to be charged.</p>
            {canMakeBill && can("bill.edit") && (
              <Button primary loading={busy} disabled={busy} onClick={billState.create}>
                <Icon name="plus" />Make bill
              </Button>
            )}
          </Segment>
          <div className="bhu-bill-side">
            <Section title="Payments" icon="rupee sign">{payments}</Section>
          </div>
          <BillRevisions bookingId={booking.id} currentBillId={null} />
        </div>
      )}

      {bill && (
        <div className="bhu-bill-body">
          <div>
            {bill.isStale && bill.status === "DRAFT" && (
              <Message warning>
                <Icon name="exclamation triangle" />
                The booking has changed since these charges were worked out (a room, a time or the rates).
                {editable && (
                  <Button size="small" primary style={{ marginLeft: 10 }} loading={busy} disabled={busy}
                    onClick={billState.refresh}>Refresh charges</Button>
                )}
              </Message>
            )}
            {bill.isStale && bill.status === "FINALIZED" && (
              <Message warning content="The booking has changed since this bill was finalized. Correct it (new revision) if the charges need to follow." />
            )}
            {bill.warnings.map((w) => <Message key={w} size="small" info content={w} />)}

            <Section title="Room rent" icon="bed"
              action={editable && !bill.isStale && (
                <Button size="mini" basic disabled={busy} onClick={billState.refresh}>
                  <Icon name="refresh" />Recalculate
                </Button>
              )}>
              <BillRoomCharges bill={bill} editable={editable} busy={busy}
                onSetRate={(lineId, rate) => billState.updateLine(lineId, { unit_price: rate })}
                onResetRate={(lineId) => billState.updateLine(lineId, { rate_overridden: false })} />
              <BillRetention bill={bill} editable={editable} busy={busy} onDecide={billState.decideRetention} />
            </Section>

            <Section title="Meals, day by day" icon="food">
              <BillMealsGrid key={bill.id} bill={bill} items={items} editable={editable} busy={busy}
                onSave={billState.saveDays} onSaveLumpSum={billState.setBoardingLumpSum} />
            </Section>

            <Section title="Other charges" icon="add">
              <BillExtras bill={bill} items={items} editable={editable} busy={busy}
                onAdd={billState.addLine} onDelete={billState.deleteLine} />
            </Section>

            <BilledTo bill={bill} editable={editable} busy={busy} onSave={billState.update} />
            <BillRevisions bookingId={booking.id} currentBillId={bill.id} />
          </div>

          <div className="bhu-bill-side">
            <Section title="Totals" icon="calculator">
              <BillTotals bill={bill} paymentSummary={billState.paymentSummary} />
            </Section>
            <Section title="Payments" icon="rupee sign">{payments}</Section>
          </div>
        </div>
      )}

      {confirmFinal && (
        <Modal open size="tiny" onClose={() => setConfirmFinal(false)}>
          <Modal.Header>Finalize bill {bill.billNumber}?</Modal.Header>
          <Modal.Content>
            The total of <strong>{formatRupees(bill.grandTotal)}</strong> will be locked, and the booking's rooms and
            dates can no longer be changed. Mistakes are fixed afterwards by issuing a new revision.
          </Modal.Content>
          <Modal.Actions>
            <Button onClick={() => setConfirmFinal(false)}>Not yet</Button>
            <Button positive loading={busy} disabled={busy}
              onClick={async () => { await billState.finalize(); setConfirmFinal(false); }}>Finalize</Button>
          </Modal.Actions>
        </Modal>
      )}
      {reasonFor && (
        <ReasonModal
          title={reasonFor === "cancel" ? `Cancel bill ${bill.billNumber}?` : `Correct bill ${bill.billNumber}?`}
          explain={reasonFor === "cancel"
            ? "The bill is kept, marked cancelled. A new bill can be made for the booking afterwards."
            : `This bill is cancelled and revision ${bill.revision + 1} is opened as a draft, keeping the meals, other charges and retention answers. Payments carry over.`}
          confirmText={reasonFor === "cancel" ? "Cancel bill" : "Open new revision"}
          busy={busy}
          onClose={() => setReasonFor(null)}
          onConfirm={async (reason) => {
            const ok = reasonFor === "cancel" ? await billState.cancel(reason) : await billState.regenerate(reason);
            if (ok) setReasonFor(null);
          }}
        />
      )}
    </div>
  );
}

function Section({ title, icon, action, children }) {
  return (
    <Segment>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Header as="h4" style={{ margin: 0 }}><Icon name={icon} />{title}</Header>
        {action || null}
      </div>
      <div style={{ marginTop: 10 }}>{children}</div>
    </Segment>
  );
}

function ReasonModal({ title, explain, confirmText, busy, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  return (
    <Modal open size="tiny" onClose={onClose}>
      <Modal.Header>{title}</Modal.Header>
      <Modal.Content>
        <p>{explain}</p>
        <Form>
          <Form.TextArea id="bill-reason" label="Reason (kept in the record)" rows={2} value={reason}
            onChange={(_, d) => setReason(d.value)} />
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>Back</Button>
        <Button negative disabled={!reason.trim() || busy} loading={busy}
          onClick={() => onConfirm(reason.trim())}>{confirmText}</Button>
      </Modal.Actions>
    </Modal>
  );
}

function BilledTo({ bill, editable, busy, onSave }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const set = (key) => (_, d) => setForm((f) => ({ ...f, [key]: d.value }));
  const edit = editable && !open && (
    <Button size="mini" basic onClick={() => {
      setForm({ billed_to_name: bill.billedToName, billed_to_address: bill.billedToAddress,
        billed_to_gstin: bill.billedToGstin, notes: bill.notes });
      setOpen(true);
    }}><Icon name="pencil" />Change</Button>
  );
  return (
    <Section title="Billed to" icon="address card outline" action={edit}>
      {open ? (
        <Form onSubmit={async () => { if (await onSave(form)) setOpen(false); }}>
          <Form.Group widths="equal">
            <Form.Input id="billed-name" label="Name" value={form.billed_to_name} onChange={set("billed_to_name")} />
            <Form.Input id="billed-gstin" label="Their GSTIN (optional)" maxLength={15} value={form.billed_to_gstin}
              onChange={set("billed_to_gstin")} />
          </Form.Group>
          <Form.TextArea id="billed-address" label="Address" rows={2} value={form.billed_to_address}
            onChange={set("billed_to_address")} />
          <Form.TextArea id="billed-notes" label="Notes printed on the bill" rows={2} value={form.notes}
            onChange={set("notes")} />
          <Button type="submit" primary size="small" disabled={busy}>Save</Button>
          <Button type="button" size="small" onClick={() => setOpen(false)}>Cancel</Button>
        </Form>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", rowGap: 4 }}>
          <span style={{ opacity: 0.6 }}>Name</span><strong>{bill.billedToName || "—"}</strong>
          <span style={{ opacity: 0.6 }}>GSTIN</span><span>{bill.billedToGstin || "—"}</span>
          <span style={{ opacity: 0.6 }}>Address</span><span style={{ whiteSpace: "pre-line" }}>{bill.billedToAddress || "—"}</span>
          <span style={{ opacity: 0.6 }}>Notes on the bill</span><span style={{ whiteSpace: "pre-line" }}>{bill.notes || "—"}</span>
        </div>
      )}
    </Section>
  );
}
